import React, { useContext, useEffect, useRef, useState, useMemo } from "react";
import { Accordion, Card } from "react-bootstrap";
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  LayersControl,
  Marker,
  Popup,
  useMap,
  GeoJSON
} from "react-leaflet";
import L from "leaflet";
import {
  getData,
  postDataWithBody,
} from "../../../Services/Services";
import { geteventdetails, getincidentcatcreate, gisStateCoordinates, getAllDistricts, gisCoordinatesData } from "../../../Services/EndPoints";
import { ChevronDown, ChevronUp, Layers  } from 'react-bootstrap-icons';
import { toast } from "react-toastify";
import { ThemeContext } from "../../..";
import Loader from "react-spinner-loader";

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom incident marker icon
const incidentIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'incident-marker'
});

// Helper component to change map center dynamically
const MapCenter = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center.length === 2) {
      map.setView(center, 12);
    }
  }, [center, map]);
  return null;
};

// Updated LayerController - removed showDistricts dependency
const LayerController = ({ hasEvents }) => {
  const map = useMap();

  useEffect(() => {
    const layerControl = document.querySelector('.leaflet-control-layers');
    if (layerControl) {
      const eventsCheckbox = layerControl.querySelectorAll('input[type="checkbox"]')[1]; // Events is second checkbox
      if (eventsCheckbox) {
        eventsCheckbox.checked = hasEvents;
        eventsCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }, [hasEvents, map]);

  return null;
};

const OpenLayerSource = () => {
  const [incidentid, setIncidentid] = useState("");
  const [incident, setIncident] = useState([]);
  const [allIncidents, setAllIncidents] = useState([]);
  const [selectedIncidentId, setSelectedIncidentId] = useState("");
  const [incidentCenter, setIncidentCenter] = useState(null);
  const [selectedIncident, setSelectedIncident] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [hasEvents, setHasEvents] = useState(false);
  const [expandedIncidentId, setExpandedIncidentId] = useState(null);
  const markerRefs = useRef({});
  const [accordionKey, setAccordionKey] = useState("0");
  const [stateGeoJSON, setStateGeoJSON] = useState(null);
  const { theme } = useContext(ThemeContext);
  const [showLayerControl, setShowLayerControl] = useState(false);
  const layerControlRef = useRef(null);

  // District states
  const [districtList, setDistrictList] = useState([]);
  const [districtLayers, setDistrictLayers] = useState({});
  const [showDistricts, setShowDistricts] = useState(true); // Controlled state

  // Loader states
  const [incidentLoader, setIncidentLoader] = useState(false);
  const [districtLoader, setDistrictLoader] = useState(false);
  const [stateLoader, setStateLoader] = useState(false);
  const [eventLoader, setEventLoader] = useState(false);

  const mapRef = useRef(null);

  useEffect(() => {
    loadIncidents();
    mapStateCoordinates();
    loadDistrictLayers();
    
    const timer = setTimeout(() => {
      if (layerControlRef.current) {
        const container = document.querySelector('.leaflet-control-layers');
        if (container) {
          container.style.display = 'none';
        }
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // NEW: Control district layer visibility
  useEffect(() => {
    if (mapRef.current && Object.keys(districtLayers).length > 0) {
      const map = mapRef.current;
      
      // Toggle all district layers
      Object.values(map._layers || {}).forEach(layer => {
        if (layer instanceof L.GeoJSON && layer.options && layer.options.style && layer.options.style.color === "#000000") {
          if (showDistricts) {
            if (!map.hasLayer(layer)) {
              map.addLayer(layer);
            }
          } else {
            if (map.hasLayer(layer)) {
              map.removeLayer(layer);
            }
          }
        }
      });
    }
  }, [showDistricts, districtLayers]);

  // Load district layers function
  const loadDistrictLayers = async () => {
    try {
      setDistrictLoader(true);
      const districtsResponse = await getData(getAllDistricts);

      if (districtsResponse && Array.isArray(districtsResponse)) {
        setDistrictList(districtsResponse);

        const districtPromises = districtsResponse.map(async (district) => {
          try {
            const response = await getData(gisCoordinatesData + district.gis_id);
            if (response && response.length > 0) {
              return {
                gisId: district.gis_id,
                geoJson: JSON.parse(response[0].coordinates),
                name: district.value
              };
            }
            return null;
          } catch (error) {
            console.error(`Error fetching district ${district.gis_id}:`, error);
            return null;
          }
        });

        const districtResults = await Promise.all(districtPromises);
        const newDistrictLayers = {};
        districtResults.forEach(result => {
          if (result) {
            newDistrictLayers[result.gisId] = {
              geoJson: result.geoJson,
              name: result.name
            };
          }
        });

        setDistrictLayers(newDistrictLayers);

        // Zoom to state bounds if available
        if (stateGeoJSON && mapRef.current) {
          const map = mapRef.current;
          const tempLayer = L.geoJSON(stateGeoJSON);
          const bounds = tempLayer.getBounds();
          map.flyToBounds(bounds, {
            padding: [50, 50],
            duration: 1,
            maxZoom: 8
          });
        }
      }
    } catch (error) {
      console.error("Error loading district layers:", error);
      toast.error("Failed to load district data");
    } finally {
      setDistrictLoader(false);
    }
  };

  const toggleLayerControl = () => {
    const container = document.querySelector('.leaflet-control-layers');
    if (container) {
      if (container.style.display === 'none' || !container.style.display) {
        container.style.display = 'block';
        container.style.marginRight = '50px';
        setShowLayerControl(true);
      } else {
        container.style.display = 'none';
        setShowLayerControl(false);
      }
    }
  };

  // Toggle district visibility
  const toggleDistricts = () => {
    setShowDistricts(prev => !prev);
  };

  const mapStateCoordinates = async () => {
    try {
      setStateLoader(true);
      const res = await getData(gisStateCoordinates + 52);
      if (res && res.length > 0) {
        const stateData = res[0];
        const geoJsonData = {
          type: "Feature",
          properties: {
            name: stateData.state_name,
            state_id: stateData.state_id,
          },
          geometry: JSON.parse(stateData.coordinates),
        };
        setStateGeoJSON(geoJsonData);
      }
    } catch (error) {
      console.error("Error fetching state coordinates:", error);
      toast.error("Failed to load state coordinates");
    } finally {
      setStateLoader(false);
    }
  };

  const formatDateDisplay = (dateTimeString) => {
    if (!dateTimeString) return "-";
    try {
      const date = new Date(dateTimeString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const formattedDate = `${day}-${month}-${year}`;
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const formattedTime = `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
      return `${formattedDate} ${formattedTime}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateTimeString;
    }
  };

  const loadIncidents = async () => {
    try {
      setIncidentLoader(true);
      const activeincidents = await getData(getincidentcatcreate);

      if (activeincidents && Array.isArray(activeincidents) && activeincidents.length > 0) {
        const firstIncidentId = activeincidents[0].id;
        setAllIncidents(activeincidents);
        setExpandedIncidentId(firstIncidentId);
        handleIncidentClick(firstIncidentId);
      } else if (activeincidents?.data && Array.isArray(activeincidents.data)) {
        const firstIncidentId = activeincidents.data[0].id;
        setAllIncidents(activeincidents.data);
        setExpandedIncidentId(firstIncidentId);
        handleIncidentClick(firstIncidentId);
      }
    } catch (error) {
      console.error("Error loading incidents:", error);
    } finally {
      setIncidentLoader(false);
    }
  };

  const handleIncidentClick = async (id) => {
    try {
      setEventLoader(true);
      setIncidentid(id);
      setSelectedIncidentId(id);
      setCurrentImageIndex(0);

      const incidentdetails = await postDataWithBody(geteventdetails, [id], {});
      console.log("event details:", incidentdetails);

      setIncident(incidentdetails);
      const hasEventsData = incidentdetails && incidentdetails.length > 0;
      setHasEvents(hasEventsData);

      if (hasEventsData) {
        setSelectedIncident(incidentdetails);
        if (incidentdetails.length > 0) {
          const lat = parseFloat(incidentdetails[0]?.lat);
          const lng = parseFloat(incidentdetails[0]?.lng);
          if (!isNaN(lat) && !isNaN(lng)) {
            setIncidentCenter([lat, lng]);
          } else {
            setIncidentCenter(null);
          }
        }
      } else {
        setSelectedIncident([]);
        setIncidentCenter(null);
      }
    } catch (error) {
      console.error("Failed to load incident details:", error);
      setHasEvents(false);
      setSelectedIncident([]);
      setIncidentCenter(null);
    } finally {
      setEventLoader(false);
    }
  };

  const getMapCenter = () => {
    return [25.5941, 85.1376];
  };

  const renderMediaCarousel = (files, eventIndex) => {
    if (!files || files.length === 0) return null;

    return (
      <div style={{ position: 'relative', marginTop: '10px' }}>
        {files.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCurrentImageIndex(prev =>
                  prev === 0 ? files.length - 1 : prev - 1
                );
              }}
              style={{
                position: 'absolute',
                left: '5px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.7)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                cursor: 'pointer',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              &lt;
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCurrentImageIndex(prev =>
                  prev === files.length - 1 ? 0 : prev + 1
                );
              }}
              style={{
                position: 'absolute',
                right: '5px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.7)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                cursor: 'pointer',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              &gt;
            </button>
          </>
        )}

        {files.map((file, index) => {
          if (!file || !file.filePath || index !== currentImageIndex) return null;

          const isVideo = /\.(mp4|webm|ogg)$/i.test(file.filePath);

          return (
            <div key={file.fileId || index}>
              {isVideo ? (
                <video
                  controls
                  style={{
                    width: '100%',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '4px'
                  }}
                >
                  <source src={file.filePath} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={file.filePath}
                  alt={`Attachment ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '4px'
                  }}
                />
              )}
              {files.length > 1 && (
                <div style={{
                  textAlign: 'center',
                  marginTop: '5px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  {currentImageIndex + 1} / {files.length}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const toggleIncidentExpand = (id) => {
    if (expandedIncidentId === id) {
      setExpandedIncidentId(null);
      setSelectedIncident([]);
      setHasEvents(false);
    } else {
      setExpandedIncidentId(id);
      handleIncidentClick(id);
    }
  };

  const handleNavigateToEvent = (eventId, lat, lng) => {
    if (!lat || !lng) return;

    const map = mapRef.current;
    const leafletMap = map?.leafletElement || map?._leaflet_map || map;

    if (leafletMap && leafletMap.flyTo) {
      leafletMap.flyTo([lat, lng], 14, {
        animate: true,
        duration: 1.2
      });
    }

    const markerRef = markerRefs.current[eventId];
    if (markerRef && markerRef.openPopup) {
      setTimeout(() => {
        markerRef.openPopup();
      }, 800);
    }
  };

  return (
    <div className="d-flex" style={{ height: "100vh", position: "relative" }}>
      <Loader 
        show={incidentLoader || districtLoader || stateLoader || eventLoader} 
        type="body" 
        message="Loading Data" 
      />
      
      {/* Left side map */}
      <div style={{ flex: 1, position: "relative" }}>
        <MapContainer
          ref={mapRef}
          center={getMapCenter()}
          zoom={7}
          minZoom={4}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
        >
          {/* Custom Layer Control Button */}
          <div className="leaflet-top leaflet-right">
            <div className="leaflet-control leaflet-bar">
              <button
                onClick={toggleLayerControl}
                style={{
                  backgroundColor: theme === "light" ? "#ffffff" : "#2c3e50",
                  border: `1px solid ${theme === "light" ? "#cccccc" : "#34495e"}`,
                  color: theme === "light" ? "#333333" : "#ecf0f1",
                  width: "36px",
                  height: "36px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  borderRadius: "4px",
                  boxShadow: "0 1px 5px rgba(0,0,0,0.4)",
                  transition: "all 0.2s ease"
                }}
                title="Toggle Layers"
              >
                <Layers size={20} />
              </button>
            </div>
          </div>

          <LayersControl
            ref={layerControlRef}
            position="topright"
            collapsed={false}
          >
            <LayersControl.BaseLayer name="Road View">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="Satellite View">
              <TileLayer
                attribution=""
                url="https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                maxZoom={20}
                subdomains={["mt0", "mt1", "mt2", "mt3"]}
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer checked name="Terrain View">
              <TileLayer
                attribution=""
                url="https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}"
                maxZoom={20}
                subdomains={["mt0", "mt1", "mt2", "mt3"]}
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="Hybrid View">
              <TileLayer
                attribution=""
                url="https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                maxZoom={20}
                subdomains={["mt0", "mt1", "mt2", "mt3"]}
              />
            </LayersControl.BaseLayer>

            {/* State Boundary - Always visible */}
            {stateGeoJSON && (
              <GeoJSON
                key="state-boundary"
                data={stateGeoJSON}
                style={{
                  color: "#000000",
                  weight: 2,
                  fillColor: "transparent",
                  fillOpacity: 0.2,
                }}
                onEachFeature={(feature, layer) => {
                  if (feature.properties && feature.properties.name) {
                    layer.bindPopup(`State: ${feature.properties.name}`);
                  }
                }}
              />
            )}

            {/* NEW: Always-rendered District layers (controlled by useEffect) */}
            <FeatureGroup>
              {Object.keys(districtLayers).length > 0 &&
                Object.entries(districtLayers).map(([gisId, districtData]) => (
                  <GeoJSON
                    key={`district-${gisId}`}
                    data={districtData.geoJson}
                    style={{
                      color: "#000000",
                      weight: 1,
                      opacity: 1,
                      fillColor: "transparent",
                      fillOpacity: 0.3,
                      interactive: true,
                    }}
                    eventHandlers={{
                      click: (e) => {
                        e.originalEvent.preventDefault();
                        e.originalEvent.stopPropagation();
                        const bounds = e.target.getBounds();
                        if (mapRef.current) {
                          mapRef.current.flyToBounds(bounds, {
                            padding: [50, 50],
                            duration: 1
                          });
                        }
                      },
                      mouseover: (e) => {
                        const layer = e.target;
                        layer.setStyle({ weight: 2, color: "#000000" });
                      },
                      mouseout: (e) => {
                        const layer = e.target;
                        layer.setStyle({ weight: 1, color: "#000000" });
                      }
                    }}
                    onEachFeature={(feature, layer) => {
                      const districtName = districtData.name || `District ${gisId}`;
                      layer.bindTooltip(`<b>${districtName}</b>`, {
                        permanent: false,
                        direction: 'top',
                        className: 'map-tooltip',
                        sticky: true,
                      });
                      layer.bindPopup(`<b>${districtName}</b>`);
                    }}
                  />
                ))}
            </FeatureGroup>

            {/* NEW: Static checkbox for districts (no children) */}
            <LayersControl.Overlay checked={showDistricts} name="Districts">
              {/* Empty - purely for checkbox control */}
            </LayersControl.Overlay>

            {/* Events Overlay */}
            <LayersControl.Overlay checked={hasEvents} name="Events">
              <FeatureGroup key={selectedIncidentId}>
                {hasEvents && selectedIncident && selectedIncident.length > 0 &&
                  selectedIncident.map((incident, index) => {
                    const lat = parseFloat(incident?.lat);
                    const lng = parseFloat(incident?.lng);

                    if (isNaN(lat) || isNaN(lng)) {
                      console.warn(`Skipping invalid coordinates for incident index ${index}`, incident);
                      return null;
                    }

                    const coordinates = [lat, lng];
                    const markerId = incident.id || index;

                    return (
                      <Marker
                        key={markerId}
                        position={coordinates}
                        icon={incidentIcon}
                        ref={(ref) => {
                          if (ref) markerRefs.current[markerId] = ref;
                        }}
                      >
                        <Popup maxWidth={300} maxHeight={400} className="custom-popup">
                          <div style={{ minWidth: '250px', maxHeight: '350px', overflowY: 'auto' }}>
                            <div style={{ marginBottom: '8px', fontSize: '13px' }}>
                              <strong>Incident Name</strong> {incident.incidentName || "NA"}
                            </div>
                            <div style={{ marginBottom: '8px', fontSize: '13px' }}>
                              <strong>Incident Category:</strong> {incident.incidentCategory || "N/A"}
                            </div>
                            <div style={{ marginBottom: '8px', fontSize: '13px' }}>
                              <strong>Event Date & Time:</strong> {formatDateDisplay(incident.eventTime)}
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                              <strong>Event Description:</strong><br />
                              <div style={{ fontSize: '13px', marginTop: '3px' }}>
                                {incident.action || "No description"}
                              </div>
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                              <strong>Lat/Long :</strong> {`${incident.lat ? incident.lat : "NA"}, ${incident.lng ? incident.lng : "NA"}`}
                            </div>
                            <div style={{ marginBottom: '8px', fontSize: '13px' }}>
                              <strong>User Details:</strong> {incident.userName + "," + incident.jobTitle + "," + incident.mobileNumber}
                            </div>
                            {incident.files && incident.files.length > 0 && renderMediaCarousel(incident.files, index)}
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })
                }
              </FeatureGroup>
            </LayersControl.Overlay>
          </LayersControl>

          <MapCenter center={incidentCenter} />
          <LayerController hasEvents={hasEvents} />
        </MapContainer>
      </div>

      {/* Right side panel */}
      <div
        style={{
          width: "400px",
          background: theme === "light" ? "#ffffff" : "#1f2a44",
          color: theme === "light" ? "#000000" : "white",
          padding: "10px",
          overflowY: "auto",
          borderRadius: '10px'
        }}
      >
        {/* District Toggle Switch - UNCOMMENTED */}
        {/* <div style={{ marginBottom: '15px' }}>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="districtToggle"
              checked={showDistricts}
              onChange={toggleDistricts}
            />
            <label
              className="form-check-label"
              htmlFor="districtToggle"
              style={{ color: theme === "light" ? "#000000" : "white" }}
            >
              Show District Boundaries
            </label>
          </div>
        </div> */}

        <Accordion activeKey={accordionKey} onSelect={(key) => {
          setAccordionKey(key);
          if (!key) {
            setExpandedIncidentId(null);
            setSelectedIncident([]);
            setIncident([]);
            setHasEvents(false);
            setIncidentCenter(null);
          }
        }}>
          <Accordion.Item eventKey="0">
            <Accordion.Header>All Incidents</Accordion.Header>
            <Accordion.Body>
              {allIncidents.length > 0 ? (
                allIncidents.map((inc) => (
                  <Card
                    key={inc.id}
                    style={{
                      marginBottom: "10px",
                      cursor: "pointer",
                      border: inc.id === expandedIncidentId ? "2px solid orange" : "1px solid #ccc",
                      background: inc.id === expandedIncidentId ? "#142a5c" : "#1e335c",
                      color: "white",
                    }}
                    onClick={() => toggleIncidentExpand(inc.id)}
                  >
                    <Card.Body style={{ position: 'relative' }}>
                      <div className="row">
                        <div className="col-10">
                          <label style={{ fontSize: '15px', color: theme === "light" ? "#000000" : "white" }}>Incident Name</label>
                          <div style={{ color: theme === "light" ? "#000000" : "white" }}>{inc.name?.trim() || "NA"}</div>
                        </div>
                        <div className="col-2 d-flex justify-content-end align-items-start">
                          {expandedIncidentId === inc.id ? (
                            <ChevronUp size={18} color={theme === "light" ? "#000000" : "white"} />
                          ) : (
                            <ChevronDown size={18} color={theme === "light" ? "#000000" : "white"} />
                          )}
                        </div>
                        <div className="col-12">
                          <label style={{ fontSize: '15px', color: theme === "light" ? "#000000" : "white" }}>Incident Category</label>
                          <div style={{color: theme === "light" ? "#000000" : "white"}}>{inc.cat?.trim() || inc.category?.trim() || "NA"}</div>
                        </div>
                        <div className="col-12">
                          <label style={{ fontSize: '15px', color: theme === "light" ? "#000000" : "white" }}>Incident Date & Time</label>
                          <div style={{color: theme === "light" ? "#000000" : "white"}}>{inc.created_on?.trim() ? formatDateDisplay(inc.created_on) : "NA"}</div>
                        </div>
                        <div className="col-12">
                          <label style={{ fontSize: '15px', color: theme === "light" ? "#000000" : "white" }}>Incident Description</label>
                          <div style={{color: theme === "light" ? "#000000" : "white"}}>{inc.description?.trim() || "NA"}</div>
                        </div>
                      </div>

                      {/* Expanded Event Data */}
                      {expandedIncidentId === inc.id && (
                        <div className="mt-3">
                          <h6 style={{ 
                            color: theme === "light" ? "#000000" : "white", 
                            marginBottom: '10px', 
                            borderBottom: '1px solid #3c6eff', 
                            paddingBottom: '5px', 
                            fontWeight: 'bold' 
                          }}>
                            EVENTS
                          </h6>

                          {incident.length > 0 ? (
                            incident.map((event, index) => (
                              <div
                                key={index}
                                style={{
                                  position: 'relative',
                                  marginTop: '12px',
                                  padding: '12px',
                                  borderRadius: '10px',
                                  background: theme === "light" ? "#ffffff" : "#1f2a44",
                                  border: '1px solid #3c6eff',
                                  boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                                  fontSize: '12px',
                                  color: theme === "light" ? "#000000" : "white"
                                }}
                              >
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '5px',
                                    right: '5px',
                                    backgroundColor: '#3c6eff',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '28px',
                                    height: '28px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '13px',
                                    boxShadow: '0 0 6px rgba(0, 0, 0, 0.3)'
                                  }}
                                >
                                  {index + 1}
                                </div>

                                <div style={{ marginBottom: '8px' }}>
                                  <strong>Event Date & Time :</strong> {event.eventTime?.trim() ? formatDateDisplay(event.eventTime) : "NA"}
                                </div>

                                <div style={{ marginBottom: '8px' }}>
                                  <strong>Event Description :</strong><br />
                                  {event.action?.trim() ? event.action : "NA"}
                                </div>

                                <div style={{ marginBottom: '8px' }}>
                                  <strong>Lat/Long :</strong>
                                  {`${event.lat ?? "NA"}, ${event.lng ?? "NA"}`}
                                </div>

                                <div style={{ marginBottom: '8px' }}>
                                  <strong>User Details :</strong> {
                                    [
                                      event.userName?.trim() || "NA",
                                      event.jobTitle?.trim() || "NA",
                                      event.mobileNumber?.trim() || "NA"
                                    ].join(", ")
                                  }
                                </div>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNavigateToEvent(
                                      event.id || index,
                                      parseFloat(event.lat),
                                      parseFloat(event.lng)
                                    );
                                  }}
                                  style={{
                                    marginTop: '10px',
                                    padding: '6px 12px',
                                    backgroundColor: '#3c6eff',
                                    border: 'none',
                                    color: 'white',
                                    borderRadius: '5px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  View Event Location On Map
                                </button>
                              </div>
                            ))
                          ) : (
                            <div style={{ fontStyle: 'italic', color: '#ccc', marginTop: '8px' }}>
                              No events available.
                            </div>
                          )}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ))
              ) : (
                <div>No incidents found</div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
    </div>
  );
};

export default OpenLayerSource;
